import {
  type HttpValidationError,
  type QuoteReviewPublic,
  QuoteReviewsService,
} from "@common/client"
import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import type { AxiosError } from "axios"
import { makeAxiosMutation, makeQuery, unwrap } from "../utils"

type ReviewError = AxiosError<HttpValidationError>

type SessionInput = { token: string; "x-review-session": string }

export const quoteReviewLinkKey = (token: string) =>
  ["quote-review-link", token] as const

export const quoteReviewLinkQuery = makeQuery({
  serviceCall: QuoteReviewsService.link,
  queryKey: (params) => quoteReviewLinkKey(params.token),
})

export const quoteReviewKey = (token: string, session: string) =>
  ["quote-review", token, session] as const

export const quoteReviewQuery = makeQuery({
  serviceCall: QuoteReviewsService.proposal,
  queryKey: (params) =>
    quoteReviewKey(params.token, params["x-review-session"]),
})

export const useSendReviewCodeMutation = makeAxiosMutation({
  serviceCall: QuoteReviewsService.sendCode,
  invalidates: () => [],
})

export const useOpenReviewSessionMutation = makeAxiosMutation({
  serviceCall: QuoteReviewsService.openSession,
  invalidates: () => [],
})

function storeReview(
  queryClient: QueryClient,
  input: SessionInput,
  review: QuoteReviewPublic,
): void {
  queryClient.setQueryData(
    quoteReviewKey(input.token, input["x-review-session"]),
    review,
  )
}

type DecideInput = Parameters<typeof QuoteReviewsService.decide>[0]

export const useDecideReviewMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<QuoteReviewPublic, ReviewError, DecideInput>({
    mutationFn: (input) => QuoteReviewsService.decide(input).then(unwrap),
    onSuccess: (review, input) => storeReview(queryClient, input, review),
  })
}

type CommentInput = Parameters<typeof QuoteReviewsService.comment>[0]

export const useCommentReviewMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<QuoteReviewPublic, ReviewError, CommentInput>({
    mutationFn: (input) => QuoteReviewsService.comment(input).then(unwrap),
    onSuccess: (review, input) => storeReview(queryClient, input, review),
  })
}

type SignInput = Parameters<typeof QuoteReviewsService.sign>[0]

export const useSignReviewMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<QuoteReviewPublic, ReviewError, SignInput>({
    mutationFn: (input) => QuoteReviewsService.sign(input).then(unwrap),
    onSuccess: (review, input) => storeReview(queryClient, input, review),
  })
}

export const useSignedAuthorisationMutation = makeAxiosMutation({
  serviceCall: QuoteReviewsService.signedAuthorisation,
  invalidates: () => [],
})
